// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {getStatusesByIds} from 'mattermost-redux/actions/users';
import {getCurrentChannelId} from 'mattermost-redux/selectors/entities/channels';
import {getPostsInCurrentChannel} from 'mattermost-redux/selectors/entities/posts';
import {getDirectShowPreferences} from 'mattermost-redux/selectors/entities/preferences';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

import {loadCustomEmojisForCustomStatusesByUserIds} from 'actions/emoji_actions';
import store from 'stores/redux_store';
import {Constants} from 'utils/constants';
import {reportMessageToSentry} from 'utils/sentry';
import {getDmUsers} from 'mattermost-redux/selectors/entities/getDmUsers';

export function loadStatusesForChannelAndSidebar() {
    return (dispatch, getState) => {
        const state = getState();
        const statusesToLoad = {};

        const channelId = getCurrentChannelId(state);
        const postsInChannel = getPostsInCurrentChannel(state);

        if (postsInChannel) {
            const posts = postsInChannel.slice(0, state.views.channel.postVisibility[channelId] || 0);
            for (const post of posts) {
                if (post.user_id) {
                    statusesToLoad[post.user_id] = true;
                }
            }
        }

        const dmPrefs = getDirectShowPreferences(state);

        for (const pref of dmPrefs) {
            if (pref.value === 'true') {
                statusesToLoad[pref.name] = true;
            }
        }

        const currentUserId = getCurrentUserId(state);
        statusesToLoad[currentUserId] = true;

        const dmUsers = getDmUsers(state);
        const generatedIds = Object.keys(statusesToLoad);

        const idsToLoad = generatedIds.filter((id) => dmUsers.includes(id) || id === currentUserId);

        dispatch(loadStatusesByIds(idsToLoad));
    };
}

export function loadStatusesForProfilesList(users) {
    return (dispatch) => {
        if (users == null) {
            return {data: false};
        }

        const statusesToLoad = [];
        for (let i = 0; i < users.length; i++) {
            statusesToLoad.push(users[i].id);
        }

        dispatch(loadStatusesByIds(statusesToLoad));

        return {data: true};
    };
}

export function loadStatusesForProfilesMap(users) {
    return (dispatch) => {
        if (users == null) {
            return;
        }

        const statusesToLoad = [];
        for (const userId in users) {
            if ({}.hasOwnProperty.call(users, userId)) {
                statusesToLoad.push(userId);
            }
        }

        dispatch(loadStatusesByIds(statusesToLoad));
    };
}

export function loadStatusesByIds(userIds) {
    return (dispatch) => {
        if (userIds.length === 0) {
            return {data: false};
        }

        if (userIds[0] === '') {
            reportMessageToSentry('trying to load statuses with empty users');
            return {data: false};
        }

        dispatch(getStatusesByIds(userIds));
        dispatch(loadCustomEmojisForCustomStatusesByUserIds(userIds));
        return {data: true};
    };
}

/**
 *
 * @param {import('@mattermost/types/users').UserProfile[]} users
 */
export function loadProfilesMissingStatus(users) {
    return (dispatch, getState) => {
        const state = getState();
        const statuses = state.entities.users.statuses;

        const dmUsers = getDmUsers(state);

        const missingStatusByIds = users.
            filter((user) => !statuses[user.id]).
            filter((user) => dmUsers.includes(user.id)).
            map((user) => user.id);

        if (missingStatusByIds.length === 0) {
            return {data: false};
        }

        dispatch(getStatusesByIds(missingStatusByIds));
        dispatch(loadCustomEmojisForCustomStatusesByUserIds(missingStatusByIds));
        return {data: true};
    };
}

let intervalId = '';

export function startPeriodicStatusUpdates() {
    clearInterval(intervalId);

    intervalId = setInterval(
        () => {
            store.dispatch(loadStatusesForChannelAndSidebar());
        },
        Constants.STATUS_INTERVAL,
    );
}

export function stopPeriodicStatusUpdates() {
    clearInterval(intervalId);
}
